import api from '../api';

/**
 * Servicio API para Declaración de Renta F-22.
 * Todas las operaciones están vinculadas a id_company + año + régimen.
 */

const BASE_URL = '/api/declaracion-renta/f22';

/**
 * Obtiene la declaración guardada para una empresa, año y régimen.
 * @param {number} idCompany - ID de la empresa
 * @param {number} anioTributario - Año tributario (ej: 2026)
 * @param {string} regimen - Régimen tributario (ej: '14d3', '14d8')
 */
export const getDeclaracion = async (idCompany, anioTributario, regimen) => {
  const response = await api.get(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}`);
  return response.data;
};

/**
 * Guarda (crea o actualiza) la declaración de una empresa.
 */
export const saveDeclaracion = async (idCompany, anioTributario, regimen, formData) => {
  const response = await api.post(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}`, {
    data: formData,
  });
  return response.data;
};

/**
 * Obtiene el listado de declaraciones de una empresa (todos los años/regímenes).
 */
export const listDeclaraciones = async (idCompany) => {
  const response = await api.get(`${BASE_URL}/${idCompany}`);
  return response.data;
};

/**
 * Elimina una declaración guardada.
 */
export const deleteDeclaracion = async (idCompany, anioTributario, regimen) => {
  const response = await api.delete(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}`);
  return response.data;
};

/**
 * Obtiene el total de ventas netas del año comercial
 * (facturas_venta + boletas_sii + comprobantes).
 * @param {number} idCompany - ID de la empresa
 * @param {number} anioComercial - Año comercial (ej: 2025 para AT 2026)
 */
export const getVentasNetas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/ventas-netas/${idCompany}/${anioComercial}`);
  return response.data;
};

/**
 * Obtiene el total de compras netas del año comercial (facturas_compra).
 * @param {number} idCompany - ID de la empresa
 * @param {number} anioComercial - Año comercial (ej: 2025 para AT 2026)
 */
export const getComprasNetas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/compras-netas/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getArriendosPagados = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/arriendos-pagados/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getIDPCAT = async (idCompany, anioTributario) => {
  const response = await api.get(`/api/declaracion-renta/idpc-at/${idCompany}/${anioTributario}`);
  return response.data;
};

export const getPPMPagados = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/ppm-pagados/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getRemuneracionesBrutas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/remuneraciones-brutas/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getRemuneracionesAdeudadas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/remuneraciones-adeudadas/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getHonorariosBrutos = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/honorarios-brutos/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getHonorariosAdeudados = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/honorarios-adeudados/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getGastosGenerales = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/gastos-generales/${idCompany}/${anioComercial}`);
  return response.data;
};

/**
 * Genera PDF server-side, lo sube a R2 y retorna la URL pública.
 * @param {number} idCompany
 * @param {number} anioTributario
 * @param {string} regimen
 * @param {string} html - HTML completo del documento a convertir
 */
export const generatePdf = async (idCompany, anioTributario, regimen, html) => {
  const response = await api.post(
    `${BASE_URL}/${idCompany}/${anioTributario}/${regimen}/generate-pdf`,
    { html }
  );
  return response.data;
};
